import { POLL } from '../config';

function parseMock(url) {
  // ex) mock:success?role=companion&delay=1800
  const [scheme, query = ''] = url.split('?');
  const kind = scheme.replace('mock:', ''); // success | error | random | idle
  const params = new URLSearchParams(query);
  const role =
    params.get('role') ||
    (Math.random() < 0.5 ? 'representative' : 'companion');
  const delay = Number(params.get('delay') || 1500 + Math.random() * 1500); // 1.5~3s
  return { kind, role, delay };
}

export async function pollVpResult({
  statusUrl = POLL.STATUS_URL,
  requestId,
  intervalMs = POLL.INTERVAL_MS,
  timeoutMs = POLL.TIMEOUT_MS,
  maxConsecutiveErrors = POLL.MAX_CONSECUTIVE_ERRORS,
  signal,
  onStatus,
}) {
  const started = Date.now();
  let consecutiveErrors = 0;

  const sleep = (ms) =>
    new Promise((resolve) => {
      if (!signal) {
        setTimeout(resolve, ms);
        return;
      }
      if (signal.aborted) {
        resolve();
        return;
      }

      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      signal.addEventListener('abort', onAbort, { once: true });
    });

  // 🔹 모의 모드: config에서 STATUS_URL을 mock:* 로 주면 실제 서버 대신 아래 로직 사용
  if (String(statusUrl).startsWith('mock:')) {
    const { kind, role, delay } = parseMock(String(statusUrl));
    // random이면 60% 성공 / 40% 실패 정도로
    if (kind === 'idle') {
      // 영구 대기: 항상 pending 응답처럼 동작
      while (!signal?.aborted) {
        await sleep(intervalMs);
        // pending 상태를 계속 유지하므로 리턴/throw 없음
      }
      return { aborted: true };
    } else {
      const decide = () =>
        kind === 'random' ? Math.random() < 0.6 : kind === 'success';
      const endAt = Date.now() + delay;
      while (Date.now() < endAt) {
        if (signal?.aborted) return { aborted: true };
        await sleep(100);
      }
      if (signal?.aborted) return { aborted: true };
      if (decide()) {
        return { ok: true, data: { status: 'verified', role } };
      }
      throw new Error('verification_failed');
    }
  }

  while (!signal?.aborted) {
    if (signal?.aborted) return { aborted: true };

    const res = await fetch(statusUrl, { credentials: 'omit', signal }).catch(
      (e) => {
        if (signal?.aborted || e?.name === 'AbortError') {
          return { ok: false, _aborted: true };
        }
        return { ok: false, _err: e };
      }
    );

    if (res?._aborted) return { aborted: true };

    if (res?.ok) {
      consecutiveErrors = 0;
      if (res.status === 204) {
        if (Date.now() - started > timeoutMs) {
          throw new Error('timeout');
        }
        await sleep(intervalMs);
        continue;
      }

      const contentType = res.headers?.get?.('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('invalid_verification_status_response');
      }

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error('invalid_verification_status_response');
      }

      if (requestId && data.requestId !== requestId) {
        throw new Error('verification_request_id_mismatch');
      }

      if (!['pending', 'verified', 'error'].includes(data.status)) {
        throw new Error('invalid_verification_status_response');
      }

      onStatus?.(data);

      if (data.status === 'verified') return { ok: true, data };
      if (data.status === 'error')
        throw new Error(data.reason || 'verification_failed');
    }

    if (res && !res.ok && !res._err && res.status >= 400 && res.status < 500) {
      throw new Error(`verification_status_http_${res.status}`);
    }

    if (res?._err || (res && !res.ok && res.status >= 500)) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new Error('verification_status_unavailable');
      }
    }

    if (Date.now() - started > timeoutMs) {
      throw new Error('timeout');
    }
    await sleep(intervalMs);
  }
  return { aborted: true };
}
